<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import { computed } from 'vue'
import type { PlayerUpgrade } from '@/types/shared/player-config'
import ClockIcon from '@/components/icons/ClockIcon.vue'
import { useUpgradeCooldown } from '@/composables/game/upgrade'

const props = withDefaults(defineProps<{
  upgrade: PlayerUpgrade
  elapsedTime: number
  animationCooldown: number
  animationOffset: number
  hasCooldown?: boolean
  isHighlight?: boolean
  isLarge?: boolean
}>(), {
  hasCooldown: false,
  isHighlight: false,
  isLarge: false,
})

const cooldownPercentage = computed(() => (props.elapsedTime / props.upgrade.cooldown) * 100)
const cooldownStyle = computed(() => {
  return {
    '--cooldown-seconds': `${props.animationCooldown}s`,
    '--pgPercentage': cooldownPercentage.value,
    '--stroke-dashoffset': `${props.animationOffset}`,
    '--size': `${props.isLarge ? 120 : 56}px`,
    '--dot-size-multiplier': `${props.isLarge ? 2 : 1}`,
  }
})

const { formattedTime, remainedTime } = useUpgradeCooldown(computed(() => props.upgrade))

</script>
<template>
  <div
    class="relative "
    :class="[{'rounded-full': hasCooldown}, isLarge ? 'h-[120px] w-[120px]' : `h-[56px] ${isHighlight ? 'w-auto' : 'w-[56px]'}`]"
  >
    <img
      :class="twMerge('w-auto h-full object-cover z-[1]', hasCooldown && !isHighlight && 'rounded-full w-full relative')"
      :src="isHighlight ? upgrade.highlight?.logoUrl : upgrade.imageUrl"
    >
    <template v-if="hasCooldown && !isHighlight">
      <svg
        class="cooldown-svg"
        viewBox="0 0 36 36"
        :style="cooldownStyle"
      >
        <path
          id="cooldown-path"
          class="cooldown-border"
          d="M18 2.0845
             a 15.9155 15.9155 0 0 1 0 31.831
             a 15.9155 15.9155 0 0 1 0 -31.831"
          transform="rotate(-90 18 18)"
        />
      </svg>
      <div
        ref="cooldownOverlay"
        class="cooldown-overlay absolute top-0 left-0 w-full h-full rounded-full z-[2] flex items-center justify-center bg-black/[.45]"
        :style="cooldownStyle"
      />
      <div
        :class="twMerge('absolute top-1/2 left-1/2 z-[11] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center',
                        remainedTime < 3600 ? 'gap-y-1' : ' gap-y-0.5')"
      >
        <ClockIcon :class="isLarge ? 'w-6 h-6 min-w-[24px]' : 'w-4 h-4 min-w-[16px]'" />
        <span
          :class="twMerge('font-medium -tracking-[0.28px]',
                          isLarge ? (remainedTime < 3600 ? 'text-[22px] leading-6' : 'text-base') :
                          (remainedTime < 3600 ? 'text-sm' : 'text-[10px] leading-3')
          )"
        >{{
          formattedTime
        }}</span>
      </div>
    </template>
  </div>
</template>
<style scoped lang="scss">
@import '@/assets/styles/cooldown.scss'
</style>